import React from 'react';
import { Alert, Button, ButtonText, FormError, InviteCard, InviteList, LoaderModal, OnboardingFooter, OnboardingHeader } from 'components';
import { EmployeeRole, EmployeeType, IApiErrorPayloadWithCode, IEmployeeSite, IInvite, InvitationDisplay, SiteRole } from 'types';
import { isOrgAdmin, orgRoleDisplay, siteRoleToEmployeeRole } from 'utils/employee';

interface IViewInvitationProps {
  invite: IInvite;
  messagingReview: (messageRole?: EmployeeRole, messageEntity?: string, isMultiInvite?: boolean) => JSX.Element | string | undefined;
  apiErrors: IApiErrorPayloadWithCode;
  handleAcceptInvite: () => void;
  handleIgnoreInvite: () => void;
  displayIgnoreAlert: () => void;
  ignoreAlertVisible: boolean;
  setIgnoreAlertVisible: (visible: boolean) => void;
  isLoading: boolean;
}

export const ViewInvitation = ({ invite, messagingReview, apiErrors, handleAcceptInvite, handleIgnoreInvite, displayIgnoreAlert, ignoreAlertVisible, setIgnoreAlertVisible, isLoading }: IViewInvitationProps) => {
  // Invite List
  const invitedSitesList: InvitationDisplay[] = (() => {
    const orgName = invite?.employee?.organization.name;
    const isAdmin = invite?.employee?.type === EmployeeType.ADMIN;

    // Return organizaton admin
    if (isAdmin) {
      return [
        {
          orgName: orgName || '',
          role: SiteRole.ADMIN,
          displayRole: orgRoleDisplay(invite.employee.type) || '',
          site: orgName,
        },
      ];
    }

    return invite?.employee?.sites?.map((site: IEmployeeSite) => ({
      orgName: orgName || '',
      role: site.role,
      displayRole: orgRoleDisplay(invite.employee.type, site.role) || '',
      site: !isAdmin && (site.name || orgName),
    }));
  })();

  const isOrganizationAdmin = isOrgAdmin(invite?.employee);
  const isMultiInvite = !isOrganizationAdmin && invitedSitesList && invitedSitesList?.length > 1;
  const messageEntity: string = invitedSitesList[0].siteName ? invitedSitesList[0].siteName : invitedSitesList[0].orgName;

  // TODO: set inviteRole to the highest privileged invite role
  const inviteRole = siteRoleToEmployeeRole[invitedSitesList[0].role];
  const adminCardView = <InviteCard data-cy="admin-invite-card" role="Org Admin" entity={messageEntity as string} />;

  const invitedSitesView = invitedSitesList.map((invite, i: number) => <InviteCard data-cy="employee-invite-card" key={i} role={invite.displayRole} entity={invite.siteName || invite.orgName} />);
  return (
    <>
      <OnboardingHeader
        data-cy="accept-invitation-header"
        title={isMultiInvite ? 'Accept Invitations' : 'Accept Invitation'}
        // inviteMessaging
        message={messagingReview(inviteRole, messageEntity as string, isMultiInvite)}
        logoVisible
      />
      <InviteList data-cy="invite-list">{isOrganizationAdmin ? adminCardView : invitedSitesView}</InviteList>
      <OnboardingFooter>
        {apiErrors?.errorMessages && apiErrors.errorMessages.map((error: string, index: number) => <FormError key={`error-message-${index}`}>{error}</FormError>)}
        <Button data-cy="accept-invite-btn" label={isMultiInvite ? 'Accept invitations' : 'Accept invitation'} onClick={handleAcceptInvite} />
        <ButtonText data-cy="ignore-invite-btn" label={isMultiInvite ? 'Ignore invitations' : 'Ignore invitation'} onClick={displayIgnoreAlert} />
      </OnboardingFooter>
      <Alert
        dataCy="ignore-invitation-alert"
        isActive={ignoreAlertVisible}
        title={isMultiInvite ? 'Ignore Invitations' : 'Ignore Invitation'}
        message={isMultiInvite ? 'Are you sure you want to ignore these invitations? You may not be able to claim them later.' : 'Are you sure you want to ignore this invitation? You may not be able to claim it later.'}
        leftActionLabel="Ignore"
        onLeftAction={handleIgnoreInvite}
        rightActionLabel="Go back"
        onRightAction={() => setIgnoreAlertVisible(false)}
      />
      <LoaderModal isActive={isLoading} title={isMultiInvite ? 'Accepting Invitations' : 'Accepting Invitation'} message="One moment please..." />
    </>
  );
};
